import AppRoutes from "AppRoutes";
import { createLogger } from "logging";
import { AgendaItemDetailModel } from "models/AgendaItemDetailModel";
import {
  useAgendaItem,
  useApi,
  useBoard,
  useEditAgendaItem,
  useLayout,
  useMeeting,
  useSendToPanel,
  useToasts,
  useTranslations,
} from "providers";
import React, { PropsWithChildren, useCallback } from "react";
import { Item, Menu, Separator, TriggerEvent, useContextMenu } from "react-contexify";

import { AgendaItemsContextMenuContext } from "./AgendaItemsContextMenuContext";

const menuId = "agenda-items-context-menu";

const logger = createLogger("AgendaItemsContextMenuProvider");

export const AgendaItemsContextMenuProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const toasts = useToasts();
  const { canSendTo } = useBoard();

  const { agendaItemsApi } = useApi();
  const { reloadMeeting } = useMeeting();
  const { setSelectAgendaItemId, setConfirmDeleteAgendaItem, setAgendaItemUpdating } = useAgendaItem();
  const { setEditAgendaItemId } = useEditAgendaItem();
  const { openSendToPanel } = useSendToPanel();
  const { show } = useContextMenu({ id: menuId });
  const { handleNavClick } = useLayout();

  /*
  const onClickTasks = useCallback(
    (item: AgendaItemDetailModel) => {
      logger.debug("onClickTasks(): ", item);
      const meetingId = item.meeting?.id;
      if (!meetingId) return;
      const url = AppRoutes.toTasks(meetingId, boardName!);
      handleNavClick(url);
    },
    [boardName, navigate]
  );
  */

  const onClickDelete = useCallback(
    (item: AgendaItemDetailModel) => {
      setConfirmDeleteAgendaItem({ id: item.id, title: item.title });
    },
    [setConfirmDeleteAgendaItem]
  );

  const onClickViewHistory = useCallback(
    (item: AgendaItemDetailModel) => {
      logger.debug("onClickViewHistory(): ", item);
      const url = AppRoutes.toBackendAuditLog("AgendaItem", item.id.toString());
      handleNavClick(url);
    },
    [handleNavClick]
  );

  const showContextMenu = (event: TriggerEvent, item: AgendaItemDetailModel) => {
    logger.debug("showContextMenu(): ", item);
    setSelectAgendaItemId(item.id);
    show({ event, id: menuId, props: item });
  };

  const onClickOutdent = async (item: AgendaItemDetailModel) => {
    const id = item.id;
    const title = item.title;
    logger.debug("onClickOutdent(): ", id);

    try {
      setAgendaItemUpdating(id, true);
      await agendaItemsApi.outdent(id);
      reloadMeeting();
    } catch (error) {
      toasts.updateFailure(i18n.AgendaItem, title, error);
    } finally {
      setAgendaItemUpdating(id, false);
    }
  };

  const onClickEdit = (item: AgendaItemDetailModel) => {
    const id = item.id;
    logger.debug("onClickEdit(): ", id);
    setEditAgendaItemId(id);
  };

  const onClickSendTo = (item: AgendaItemDetailModel) => {
    const id = item.id;
    logger.debug("onClickSendTo(): ", id);
    openSendToPanel(id);
  };

  const onClickIndent = async (item: AgendaItemDetailModel) => {
    const id = item.id;
    const title = item.title;
    logger.debug("onClickIndent(): ", id);

    try {
      setAgendaItemUpdating(id, true);
      await agendaItemsApi.indent(id);
      reloadMeeting();
    } catch (error) {
      toasts.updateFailure(i18n.AgendaItem, title, error);
    } finally {
      setAgendaItemUpdating(id, false);
    }
  };

  const isDisabledViewHistory = (item: AgendaItemDetailModel) => {
    if (!item) return true;
    return !item.canViewHistory;
  };

  const isDisabledDelete = (item: AgendaItemDetailModel) => {
    if (!item) return true;
    return !item.canDelete;
  };

  const isDisabledOutdent = (item: AgendaItemDetailModel): boolean => {
    if (!item) return true;
    const { level, canEdit, isCategory, isPause } = item;

    if (isCategory) {
      logger.debug(`isDisabledOutdent(): true, outdent of "Category" not allowed`);
      return true;
    }

    if (isPause) {
      logger.debug(`isDisabledOutdent(): true, outdent of "Pause" not allowed`);
      return true;
    }

    if (level <= 0) {
      logger.debug(`isDisabledOutdent(): true, outdent<=0 not allowed`);
      return true;
    }

    return !canEdit;
  };

  const isDisabledIndent = (item: AgendaItemDetailModel): boolean => {
    if (!item) return true;
    const { level, canEdit, isCategory, isPause } = item;

    if (isCategory) {
      logger.debug(`isDisabledIndent(): true, outdent of "Category" not allowed`);
      return true;
    }

    if (isPause) {
      logger.debug(`isDisabledIndent(): true, outdent of "Pause" not allowed`);
      return true;
    }

    if (level >= 4) {
      logger.debug(`isDisabledIndent(): true, indent >5 not allowed`);
      return true;
    }

    return !canEdit;
  };

  const isDisabledSendTo = (item: AgendaItemDetailModel) => {
    if (!item) return true;
    const { canEdit, isPause, isCategory } = item;
    if (!canEdit) {
      logger.debug(`isDisabledSendTo(): true (canEdit:=false)`);
      return true;
    }

    if (isCategory) {
      logger.debug(`isDisabledSendTo(): true (Category)`);
      return true;
    }

    if (isPause) {
      logger.debug(`isDisabledSendTo(): true (Pause)`);
      return true;
    }

    if (!canSendTo) {
      logger.debug(`isDisabledSendTo(): true (doesn't have SendTo role)`);
      return true;
    }

    // SendTo own board is always enabled: https://balsamiq.cloud/sbiw67j/pbfnfkx/rB4A3
    // if (!getRequired('App.BoardSettings.HasSendToTargets')) {
    //   logger.debug(`isDisabledSendTo(): true (no sendTo targets defined)`);
    //   return true;
    // }

    return false;
  };

  const isDisabledEdit = (item: AgendaItemDetailModel) => {
    if (!item) return true;
    return !item.canEdit;
  };

  return (
    <AgendaItemsContextMenuContext.Provider
      value={{
        showContextMenu,
      }}
    >
      <Menu id={menuId} theme="sbb">
        <Item onClick={(e) => onClickOutdent(e.props)} disabled={(e) => isDisabledOutdent(e.props)}>
          {i18n.Actions_Outdent_AgendaItem}
        </Item>

        <Item onClick={(e) => onClickIndent(e.props)} disabled={(e) => isDisabledIndent(e.props)}>
          {i18n.Actions_Indent_AgendaItem}
        </Item>

        <Separator />

        <Item onClick={(e) => onClickEdit(e.props)} disabled={(e) => isDisabledEdit(e.props)}>
          {i18n.Actions_Edit_AgendaItem}
        </Item>

        <Item onClick={(e) => onClickSendTo(e.props)} disabled={(e) => isDisabledSendTo(e.props)}>
          {i18n.Actions_SendTo_AgendaItem}
        </Item>

        {/*
        <Separator />
        <Item onClick={(e) => onClickMove(e.props)} disabled={(e) => isDisabledMove(e.props)}>
          {i18n.Actions_Move_AgendaItem}
        </Item>

        <Item onClick={(e) => onClickCopy(e.props)} disabled={(e) => isDisabledCopy(e.props)}>
          {i18n.Actions_Copy_AgendaItem}
        </Item>
        */}

        {/*
        <Separator />
        <Item onClick={(e) => onClickTasks(e.props)}>{i18n.Actions_View_Tasks}</Item>
        */}

        <Separator />
        <Item onClick={(e) => onClickDelete(e.props)} disabled={(e) => isDisabledDelete(e.props)}>
          {i18n.Actions_Delete_Meeting}
        </Item>

        <Separator />
        <Item onClick={(e) => onClickViewHistory(e.props)} disabled={(e) => isDisabledViewHistory(e.props)}>
          {i18n.Actions_View_History}
        </Item>
      </Menu>

      {children}
    </AgendaItemsContextMenuContext.Provider>
  );
};
